import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot } from "vue"

import { SearchCasesRequest } from "@/models/Cases";
import { IMasonryTile } from "@/models/IMasonryTile";
import { Facet, Pagination } from "@/models/Search";
import { computed, onMounted, PropType, reactive, ref, watch } from "vue";
import { CaseApi } from "@/services/Api";

type UrlSearchParams = {
  omrade?: string | undefined;
  land?: string | undefined;
  produktkategorier?: string | undefined;
  page: number | undefined;
  pageSize: number | undefined;
};

const defaultPageSize = 18;

export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBlockWrapper',
  props: {
  totalHits: {
    type: Number,
    required: true,
  },
  initialFilters: {
    type: Array,
    default: () => [],
  },
  currentNodeId: {
    type: Number,
    required: true,
  },
  pagination: {
    type: Object as PropType<Pagination>,
    required: true,
  },
  showMaps: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;

const caseListingsElement = ref<HTMLElement | null>(null);



onMounted(() => {
  caseListingsElement.value = document.getElementById("case-listings-focus-anchor") as HTMLElement | null;
});

function moveFocusToCaseList(): void {
  caseListingsElement.value?.focus({ preventScroll: true });
}

function scrollToTopOfList(): void {
  const topOfListLocation = caseListingsElement.value?.getBoundingClientRect().top ?? 0;
  if (topOfListLocation < 0) {
    caseListingsElement.value?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

const hideInitial = ref<boolean>(false);

function useIsOpen() {
  const isOpen = ref<boolean>(false);
  const open = () => {
    isOpen.value = true;
  };
  const close = () => {
    isOpen.value = false;
  };
  const toggleOpen = () => {
    if (isOpen.value) {
      close();
    } else {
      open();
    }
  };
  return { isOpen, open, close, toggleOpen };
}

const { isOpen, close, toggleOpen } = useIsOpen();

// filter handling
function getFilterOptions(filters: Facet[]): Record<string, Facet> {
  return Object.fromEntries(filters.map((x) => [x.key, x]));
}

function getInitFilterOptions(filters: Facet[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const filter of filters) {
    const key = filter.key;
    if (filter.available.length > 0) {
      map[key] = filter.available.filter((x) => x.selected).map((x) => x.value as string);
      continue;
    }
    map[key] = filter.levels
      .flatMap((x) => x.available)
      .filter((x) => x.selected)
      .map((x) => x.value as string);
  }
  return map;
}

const initFilters = (props.initialFilters as Facet[]) || [];
const filterOptions = ref<Record<string, Facet>>(getFilterOptions(initFilters));
const activeFilters = ref<Record<string, string[]>>(getInitFilterOptions(initFilters));

function onRemoveFilter(filterGroupKey: string, id: string) {
  const index = activeFilters.value[filterGroupKey].indexOf(id);
  if (index > -1) {
    activeFilters.value[filterGroupKey].splice(index, 1);
    updateCases();
  }
}

function resetFilters() {
  activeFilters.value = {};
  updateCases();
}

const onToggleFilterClick = (filterGroupKey: string, value: string[]) => {
  currentPage.value = 1;
  activeFilters.value[filterGroupKey] = value;
  updateCases();
};

// pagination info and page handling
const paginationInfo = ref<Pagination>(props.pagination);
const pageSizeOptions = computed(() => paginationInfo.value.pageSizeOptions || [defaultPageSize]);

const pageChange = async (newPage: number): Promise<void> => {
  currentPage.value = newPage;
  await updateCases();
};

const pageSizeChange = async (newPageSize: number): Promise<void> => {
  currentPage.value = 1;
  currentPageSize.value = newPageSize;
  await updateCases();
};

const query = new URL(location.href).searchParams;
const currentPage = ref<number>(parseInt(query.get("page") ?? "") || 1);
const currentPageSize = ref<number>(parseInt(query.get("pageSize") ?? "") || defaultPageSize);
const totalPages = ref<number>();
const requestFullResult = computed(() => props.showMaps);
const caseTiles = ref<Array<IMasonryTile>>();
const totalHits = ref<number>(props.totalHits);
watch(requestFullResult, () => {
  updateCases();
});
const updateCases = async () => {
  const request: SearchCasesRequest = {
    ...activeFilters.value,
    contentId: props.currentNodeId,
    pageNo: currentPage.value,
    totalPageSize: currentPageSize.value,
    requestFullResult: requestFullResult.value,
  };

  updateUrlHistory(request);
  const response = await CaseApi.searchCases(request);
  moveFocusToCaseList();
  filterOptions.value = getFilterOptions(response?.facets);
  scrollToTopOfList();

  caseTiles.value = response.casePages;
  paginationInfo.value = response.pagination;
  currentPage.value = response.pagination.currentPage;
  totalPages.value = response.pagination.totalPages;
  totalHits.value = response.totalHits;
  hideInitial.value = true;
};

function findEncodedValue(filterKey: string, value: string): string | undefined {
  const filter = filterOptions.value[filterKey];
  if (!filter) {
    return undefined;
  }

  const encodedValueFromAvailable = filter.available.find((av) => av.value === value)?.encodedValue;

  if (encodedValueFromAvailable) {
    return encodedValueFromAvailable;
  }

  const encodedValueFromLevels = filter.levels
    .flatMap((level) => level.available)
    .find((av) => av.value === value)?.encodedValue;

  return encodedValueFromLevels;
}

function toQueryParam(filterKey: keyof SearchCasesRequest, values: string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values
    .map((value) => findEncodedValue(filterKey, value))
    .filter(Boolean)
    .join(",");
}

// update url when filters or pagination changes
function updateUrlHistory(request: SearchCasesRequest) {
  const newSearchParams: UrlSearchParams = {
    page: currentPage.value > 1 ? currentPage.value : undefined,
    pageSize: currentPageSize.value > defaultPageSize ? currentPageSize.value : undefined,
    omrade: toQueryParam("AreaTags", request.AreaTags),
    land: toQueryParam("Country", request.Country),
    produktkategorier: toQueryParam("ProductCategories", request.ProductCategories),
  };
  const url = new URL(location.href);
  Object.entries(newSearchParams).forEach(([key, value]) => {
    if (value === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value.toString());
    }
  });
  window.history.pushState(structuredClone(request), "", url);
}

const filtersModalAccordionId = ref<string>();
const onToggleFilterAccordion = (id: string) => {
  if (filtersModalAccordionId.value == id) {
    filtersModalAccordionId.value = undefined;
  } else {
    filtersModalAccordionId.value = id;
  }
};

return (_ctx: any,_cache: any) => {
  return _renderSlot(_ctx.$slots, "default", {
    filterOptions: filterOptions.value,
    activeFilters: activeFilters.value,
    isOpen: _unref(isOpen),
    onFilterClick: _unref(toggleOpen),
    onToggleFilterClick: onToggleFilterClick,
    caseTiles: caseTiles.value,
    hideInitial: hideInitial.value,
    filtersModalAccordionId: filtersModalAccordionId.value,
    onToggleFilterAccordion: onToggleFilterAccordion,
    closeFilters: _unref(close),
    resetFilters: resetFilters,
    totalHits: totalHits.value,
    onRemoveFilter: onRemoveFilter,
    pageChange: pageChange,
    pageSizeChange: pageSizeChange,
    currentPage: currentPage.value,
    currentPageSize: currentPageSize.value,
    totalPages: totalPages.value,
    pageSizeOptions: pageSizeOptions.value
  })
}
}

})